import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Paper, TextField, Button,
  MenuItem, Select, FormControl, InputLabel, LinearProgress,
  Alert, Chip, CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useAuth } from '../../../contexts/AuthContext';
import { APPLAB_ACCENT, APPLAB_ORANGE } from '../constants';
import UppdragPicker from '../components/UppdragPicker';
import { jsxToHtml } from '../lib/jsxToHtml';

const STATUS_OPTIONS = [
  { value: 'beta',          label: 'Beta' },
  { value: 'active',        label: 'Stabil' },
  { value: 'experiment',    label: 'Experiment' },
  { value: 'coming_soon',   label: 'Vilande' },
];

const ACCEPTED = '.html,.zip,.jsx,.js';
const MAX_SIZE = 10 * 1024 * 1024;

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function generateId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}

export default function UploadPage() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [appName,      setAppName]      = useState('');
  const [description,  setDescription]  = useState('');
  const [status,       setStatus]       = useState('beta');
  const [uppdrag,      setUppdrag]      = useState([]);
  const [file,         setFile]         = useState(null);
  const [converting,   setConverting]   = useState(false);
  const [dragging,     setDragging]     = useState(false);
  const [uploading,    setUploading]    = useState(false);
  const [progress,     setProgress]     = useState(0);
  const [done,         setDone]         = useState(null);
  const [error,        setError]        = useState(null);
  const inputRef = useRef();

  const handleFile = async (f) => {
    setError(null);
    setDone(null);
    if (!f) return;
    if (f.size > MAX_SIZE) { setError('Filen är för stor (max 10 MB).'); return; }
    const ext = f.name.split('.').pop().toLowerCase();
    if (!['html', 'zip', 'jsx', 'js'].includes(ext)) {
      setError('Endast .html, .zip och .jsx stöds.');
      return;
    }
    if (ext === 'jsx' || ext === 'js') {
      setConverting(true);
      try {
        const source = await f.text();
        const html   = await jsxToHtml(source, f.name);
        const blob   = new Blob([html], { type: 'text/html' });
        const htmlFile = new File([blob], f.name.replace(/\.(jsx?|tsx?)$/, '.html'), { type: 'text/html' });
        Object.defineProperty(htmlFile, 'displayName', { value: f.name });
        setFile(htmlFile);
        if (!appName.trim()) setAppName(f.name.replace(/\.(jsx?|tsx?)$/, ''));
      } catch (e) {
        setError('Kunde inte konvertera JSX-filen: ' + e.message);
      } finally {
        setConverting(false);
      }
      return;
    }
    setFile(f);
  };

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  }, [appName]); // eslint-disable-line react-hooks/exhaustive-deps

  async function upload() {
    if (!file || !appName.trim()) return;
    setUploading(true);
    setError(null);
    setProgress(10);

    try {
      const appId    = generateId();
      const b64      = await fileToBase64(file);
      setProgress(40);

      const token = await currentUser.getIdToken();
      setProgress(55);

      const res = await fetch('/.netlify/functions/applab-upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          appId,
          fileName:    file.name,
          fileBase64:  b64,
          appName:     appName.trim(),
          description: description.trim(),
          status,
          uppdrag,
        }),
      });

      setProgress(90);
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Uppladdning misslyckades.'); return; }
      setDone(data.url);
      setProgress(100);
    } catch (e) {
      setError(e.message);
    } finally {
      setUploading(false);
    }
  }

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      color: 'white', bgcolor: 'rgba(255,255,255,0.04)',
      '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
      '&:hover fieldset': { borderColor: APPLAB_ACCENT },
      '&.Mui-focused fieldset': { borderColor: APPLAB_ACCENT },
    },
    '& label': { color: '#888' },
    '& label.Mui-focused': { color: APPLAB_ACCENT },
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#111', color: 'white', pb: 6 }}>
      <Box sx={{ bgcolor: '#1a1a1a', borderBottom: '1px solid rgba(255,255,255,0.06)', px: 3, py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box onClick={() => navigate('/applab')} sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', color: '#888', '&:hover': { color: 'white' }, fontSize: '0.85rem' }}>
            <ArrowBackIcon sx={{ fontSize: 16 }} /> AppLAB
          </Box>
          <Typography sx={{ color: '#444' }}>›</Typography>
          <Typography fontWeight={700} fontSize="0.9rem">
            Ladda upp app
          </Typography>
        </Box>
      </Box>

      <Container maxWidth="sm" sx={{ mt: 5 }}>
        <Typography variant="h5" fontWeight={800} sx={{ mb: 0.5 }}>
          Ny App<span style={{ color: APPLAB_ORANGE }}>LAB</span>-app
        </Typography>
        <Typography sx={{ color: '#666', mb: 4, fontSize: '0.9rem' }}>
          Ladda upp en HTML-, JSX- eller ZIP-fil med din app.
        </Typography>

        <Paper sx={{ p: 3, bgcolor: '#1a1a1a', borderRadius: 2, mb: 2 }}>
          <TextField label="App-namn" value={appName} onChange={e => setAppName(e.target.value)}
            fullWidth size="small" sx={{ ...inputSx, mb: 2 }} />
          <TextField label="Beskrivning" value={description} onChange={e => setDescription(e.target.value)}
            fullWidth size="small" multiline rows={2} sx={{ ...inputSx, mb: 2 }} />
          <FormControl size="small" fullWidth sx={{ mb: 2 }}>
            <InputLabel sx={{ color: '#888', '&.Mui-focused': { color: APPLAB_ACCENT } }}>Status</InputLabel>
            <Select value={status} onChange={e => setStatus(e.target.value)} label="Status"
              sx={{ color: 'white', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.12)' }, '& .MuiSvgIcon-root': { color: '#888' } }}
              MenuProps={{ PaperProps: { sx: { bgcolor: '#1e1e1e', color: 'white' } } }}
            >
              {STATUS_OPTIONS.map(o => <MenuItem key={o.value} value={o.value} sx={{ color: 'white' }}>{o.label}</MenuItem>)}
            </Select>
          </FormControl>
          <UppdragPicker value={uppdrag} onChange={setUppdrag} />
        </Paper>

        {/* Drop zone */}
        <Paper
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          sx={{
            p: 4, mb: 2, borderRadius: 2, textAlign: 'center', cursor: 'pointer',
            border: `2px dashed ${dragging ? APPLAB_ACCENT : file ? APPLAB_ACCENT + '88' : 'rgba(255,255,255,0.1)'}`,
            bgcolor: dragging ? APPLAB_ACCENT + '11' : '#1a1a1a',
            transition: 'all 0.15s',
            '&:hover': { borderColor: APPLAB_ACCENT, bgcolor: APPLAB_ACCENT + '0a' },
          }}
        >
          <input ref={inputRef} type="file" accept={ACCEPTED} style={{ display: 'none' }}
            onChange={e => handleFile(e.target.files[0])} />
          {converting ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
              <CircularProgress size={32} sx={{ color: APPLAB_ACCENT }} />
              <Typography fontSize="0.82rem" sx={{ color: '#888' }}>
                Konverterar JSX — hämtar React &amp; Tailwind...
              </Typography>
            </Box>
          ) : file ? (
            <Box>
              <CheckCircleIcon sx={{ fontSize: 36, color: APPLAB_ACCENT, mb: 1 }} />
              <Typography fontWeight={700} sx={{ color: 'white' }}>
                {file.displayName || file.name}
              </Typography>
              {file.displayName && (
                <Typography fontSize="0.75rem" sx={{ color: APPLAB_ACCENT, mt: 0.25 }}>
                  Konverterad JSX → HTML
                </Typography>
              )}
              <Typography fontSize="0.8rem" sx={{ color: '#666', mt: 0.5 }}>
                {(file.size / 1024).toFixed(0)} KB · klicka för att byta
              </Typography>
            </Box>
          ) : (
            <Box>
              <CloudUploadIcon sx={{ fontSize: 40, color: '#444', mb: 1 }} />
              <Typography fontWeight={600} sx={{ color: '#888' }}>Dra hit eller klicka</Typography>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mt: 1.5 }}>
                <Chip label=".html" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.06)', color: '#666', fontSize: '0.7rem' }} />
                <Chip label=".jsx" size="small" sx={{ bgcolor: APPLAB_ACCENT + '18', color: APPLAB_ACCENT, fontSize: '0.7rem' }} />
                <Chip label=".zip" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.06)', color: '#666', fontSize: '0.7rem' }} />
              </Box>
              <Typography fontSize="0.75rem" sx={{ color: '#444', mt: 1 }}>Max 10 MB</Typography>
            </Box>
          )}
        </Paper>

        {error && <Alert severity="error" sx={{ mb: 2, bgcolor: '#2e0000', color: '#ef9a9a' }}>{error}</Alert>}

        {done ? (
          <Alert
            severity="success"
            sx={{ mb: 2, bgcolor: '#0d2b0d', color: '#81c784' }}
            action={
              <Button size="small" sx={{ color: APPLAB_ACCENT }} onClick={() => navigate('/applab')}>
                Till AppLAB
              </Button>
            }
          >
            Appen är uppladdad!
          </Alert>
        ) : (
          <Button
            variant="contained" fullWidth onClick={upload}
            disabled={uploading || !file || !appName.trim()}
            sx={{ py: 1.5, bgcolor: APPLAB_ACCENT, '&:hover': { bgcolor: '#00796B' }, fontWeight: 700 }}
          >
            {uploading ? 'Laddar upp...' : 'Ladda upp'}
          </Button>
        )}

        {uploading && (
          <LinearProgress variant="determinate" value={progress}
            sx={{ mt: 1.5, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.06)',
              '& .MuiLinearProgress-bar': { bgcolor: APPLAB_ACCENT } }} />
        )}
      </Container>
    </Box>
  );
}
